import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import {
  DatabaseIcon,
  getDatabaseSourceDisplayName,
} from '@/pages/TableViews/utils/databaseSources';
import {
  useCreateTableViewService,
  useGetTemplatesService,
  useTableViewService,
  useUseTemplateService,
} from '@/services/tableViews';
import type { Template } from '@ditto/shared';
import { Calendar, Hash, MessageSquareText, Tag, Type } from 'lucide-react';
import { useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface TemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isInitializing: boolean;
  currentTableViewId: string;
}

export const TemplatesDialog: FC<TemplatesDialogProps> = ({
  open,
  onOpenChange,
  isInitializing,
  currentTableViewId,
}) => {
  const navigate = useNavigate();
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const { templates } = useGetTemplatesService();
  const { useTemplate, isUsingTemplate } = useUseTemplateService();
  const { createTableView } = useCreateTableViewService();
  const { tableView } = useTableViewService(currentTableViewId);

  const isEmptyTableView =
    !tableView ||
    (tableView?.columns.length === 0 && tableView?.rows.length === 0);

  const handleSelectTemplate = (
    template: Template,
    createNewTable: boolean
  ) => {
    // For other templates, just create the table
    useTemplate(
      {
        templateId: template.id,
        tableViewId: createNewTable ? undefined : currentTableViewId,
      },
      {
        onSuccess: data => {
          onOpenChange(false);
          if (workspaceUname) {
            // If template has database sources, open the add items panel in database mode
            const hasDatabaseSources =
              template.databaseSources && template.databaseSources.length > 0;
            const url = `/workspace/${workspaceUname}/table-views/${data.id}${hasDatabaseSources ? '?panel=database' : ''}`;
            navigate(url);
          }
        },
      }
    );
  };

  const handleStartFromScratch = (createNewTable: boolean) => {
    if (createNewTable) {
      createTableView(
        { name: 'New Table View' },
        {
          onSuccess: data => {
            navigate(`/workspace/${workspaceUname}/table-views/${data.id}`);
          },
        }
      );
    } else {
      navigate(
        `/workspace/${workspaceUname}/table-views/${currentTableViewId}`
      );
    }
    onOpenChange(false);
  };

  const templatesList = templates;
  const isLoading = isUsingTemplate || isInitializing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] max-h-[80vh] flex flex-col overflow-hidden">
        {/* Sticky Header */}
        <DialogHeader className="shrink-0">
          <DialogTitle>Templates</DialogTitle>
          <DialogDescription>
            To begin your table, select a template or start from scratch.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable Content */}
        <FlexCol gap="6" className="flex-1 overflow-y-auto py-4">
          {templatesList.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={createNewTable =>
                handleSelectTemplate(template, createNewTable)
              }
              isLoading={isLoading}
              isEmptyTableView={isEmptyTableView}
            />
          ))}
        </FlexCol>

        {/* Sticky Footer */}
        <DialogFooter className="shrink-0 border-t border-neutral-200 pt-4">
          <Button
            variant="outline"
            onClick={() => handleStartFromScratch(!isEmptyTableView)}
            className="self-end"
          >
            Start from scratch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TemplateCardProps {
  template: Template;
  onSelect: (createNewTable: boolean) => void;
  isLoading?: boolean;
  isEmptyTableView?: boolean;
}

const TemplateCard: FC<TemplateCardProps> = ({
  template,
  onSelect,
  isLoading,
  isEmptyTableView,
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const handleUseTemplate = () => {
    if (isEmptyTableView) {
      onSelect(false);
    } else {
      setShowConfirmationDialog(true);
    }
  };

  return (
    <FlexCol
      gap="3.5"
      p="4"
      br="md"
      className="border border-neutral-200 rounded-lg"
    >
      <FlexRow className="gap-3">
        {template.databaseSources &&
          template.databaseSources.length > 0 &&
          template.databaseSources.map(source => (
            <Badge key={source} variant="outline" className="w-fit rounded-lg">
              <FlexRow alignItems="center" gap="2">
                <DatabaseIcon sourceId={source} className="h-4" />
                <Text className="text-sm font-medium">
                  {getDatabaseSourceDisplayName(source) ?? source}
                </Text>
              </FlexRow>
            </Badge>
          ))}
      </FlexRow>

      <Text variant="body-lg-bold">{template.name}</Text>
      <Text variant="body-sm" className="text-neutral-black">
        {template.description}
      </Text>
      <Text variant="body-sm" className="font-weight-500 text-black">
        Columns
      </Text>
      <FlexRow gap="2" alignItems="center" className="flex-wrap">
        {template.columns.map((column, index) => (
          <Badge
            variant="outline"
            className="w-fit bg-neutral-200 h-6 rounded-lg border-none"
            key={`${index}-${column.name}-badge`}
          >
            <FlexRow alignItems="center" gap="2" w="fit">
              {findIconForColumnType(column.type)}
              {column.name}
            </FlexRow>
          </Badge>
        ))}
      </FlexRow>

      <Button
        variant="dark"
        className="w-fit self-end"
        onClick={handleUseTemplate}
        loading={isLoading}
      >
        Use template
      </Button>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onOpenChange={setShowConfirmationDialog}
        onNewTable={() => {
          setShowConfirmationDialog(false);
          onSelect(true);
        }}
        onReplaceTable={() => {
          setShowConfirmationDialog(false);
          onSelect(false);
        }}
      />
    </FlexCol>
  );
};

const findIconForColumnType = (type: string) => {
  switch (type) {
    case 'text':
      return <Type className="w-4 h-4" />;
    case 'date':
      return <Calendar className="w-4 h-4" />;
    case 'number':
      return <Hash className="w-4 h-4" />;
    case 'verbatim':
      return <MessageSquareText className="w-4 h-4" />;
    case 'options':
      return <Tag className="w-4 h-4" />;
    default:
      return <Type className="w-4 h-4" />;
  }
};

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewTable: () => void;
  onReplaceTable: () => void;
}

const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  open,
  onOpenChange,
  onNewTable,
  onReplaceTable,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new table or replace?</DialogTitle>
        </DialogHeader>
        <FlexCol gap="2">
          <Text variant="body-sm" className="font-weight-500 text-neutral-500">
            You have data in your current table. <br /> Create new table: Keep
            existing data and start fresh with this template. <br /> Replace
            table: Overwrite current data with the template (cannot be undone).
          </Text>
        </FlexCol>
        <FlexRow gap="2">
          <Button variant="outline" onClick={onNewTable}>
            Create new table
          </Button>
          <Button variant="dark" onClick={onReplaceTable}>
            Replace table
          </Button>
        </FlexRow>
      </DialogContent>
    </Dialog>
  );
};
